/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BreadcrumbItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
  showHomeIcon?: boolean;
}

export function Breadcrumbs({ items, className, showHomeIcon = true }: BreadcrumbsProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (items.length === 0) return null;

  // Determine which items to show on mobile (collapse middle items)
  const getDisplayItems = () => {
    if (isExpanded || items.length <= 3) {
      return items;
    }

    // Show first, ellipsis, and last two items
    return [
      items[0],
      { label: "...", href: "", isEllipsis: true } as BreadcrumbItem & { isEllipsis?: boolean },
      ...items.slice(-2),
    ];
  };

  const displayItems = getDisplayItems();

  return (
    <nav aria-label="Breadcrumb" className={cn("flex items-center", className)}>
      <ol className="flex items-center gap-1 text-sm">
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          const isFirst = index === 0;
          const isEllipsis = (item as BreadcrumbItem & { isEllipsis?: boolean }).isEllipsis;
          const Icon = item.icon;

          return (
            <li key={item.href || `ellipsis-${String(index)}`} className="flex items-center gap-1">
              {/* Separator */}
              {index > 0 && (
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/50" />
              )}

              {/* Ellipsis button for collapsed items */}
              {isEllipsis ? (
                <button
                  type="button"
                  onClick={() => {
                    setIsExpanded(true);
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-muted"
                  aria-label="Show all breadcrumbs"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </button>
              ) : isLast ? (
                /* Current page - not clickable, bolder; show full label (e.g. submission number) */
                <span className="font-medium text-foreground" aria-current="page">
                  {Icon && <Icon className="mr-1.5 inline-block h-3.5 w-3.5" />}
                  {item.label}
                </span>
              ) : (
                /* Clickable link */
                <Link
                  to={item.href || ""}
                  {...(isFirst && showHomeIcon ? { "aria-label": item.label } : {})}
                >
                  <span className="flex max-w-[150px] items-center truncate text-muted-foreground transition-colors hover:text-foreground">
                    {isFirst && showHomeIcon ? (
                      <Home className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <>
                        {Icon && <Icon className="mr-1.5 h-3.5 w-3.5" />}
                        {item.label}
                      </>
                    )}
                  </span>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
